import { forwardRef, type Ref, type ReactNode, type HTMLAttributes, type CSSProperties } from 'react';
import { useAppShell, type SizingTier } from './AppShellContext.js';

export interface AppShellMainProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  /** Per-instance override of the shell's `paddingX`. */
  paddingX?: SizingTier;
  /** Per-instance override of the shell's `paddingY`. */
  paddingY?: SizingTier;
  /** Per-instance override of the shell's `maxWidth` (box layout). */
  maxWidth?: AppShellMainProps['paddingX'];
}

const SEMANTIC_SPACE = new Set(['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl']);
const SEMANTIC_BP = new Set(['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl']);

function resolvePadding(v: SizingTier): string {
  if (typeof v === 'number') return `${v}px`;
  if (SEMANTIC_SPACE.has(v as string)) return `var(--rr-spacing-${v})`;
  return v;
}

function resolveMaxWidth(v: AppShellMainProps['maxWidth']): string {
  if (v === undefined) return 'var(--rr-bp-xl)';
  if (typeof v === 'number') return `${v}px`;
  if (SEMANTIC_BP.has(v as string)) return `var(--rr-bp-${v})`;
  return v;
}

/**
 * Main content region. Honors the shell's `layout`:
 *   • `'full'` - content stretches edge-to-edge; `paddingX`/`paddingY`
 *     apply as gutters on the inside of `<main>`.
 *   • `'box'`  - content is centered in a `max-width` box; the same
 *     `paddingX`/`paddingY` apply to the *box's* gutter.
 */
export const AppShellMain = forwardRef(function AppShellMain(
  { children, paddingX, paddingY, maxWidth, className, style, ...rest }: AppShellMainProps,
  ref: Ref<HTMLElement>
): ReactNode {
  const ctx = useAppShell();
  const px = resolvePadding(paddingX ?? ctx.paddingX);
  const py = resolvePadding(paddingY ?? ctx.paddingY);
  const mw = resolveMaxWidth(maxWidth ?? ctx.maxWidth);

  if (ctx.layout === 'box') {
    return (
      <main
        ref={ref}
        className={['rr-app-shell__main', 'rr-app-shell__main--box', className].filter(Boolean).join(' ')}
        style={style}
        {...rest}
      >
        <div
          className="rr-app-shell__main-box"
          style={{
            maxWidth: mw,
            paddingInline: px,
            paddingBlock: py
          } as CSSProperties}
        >
          {children}
        </div>
      </main>
    );
  }

  return (
    <main
      ref={ref}
      className={['rr-app-shell__main', 'rr-app-shell__main--full', className].filter(Boolean).join(' ')}
      style={{ paddingInline: px, paddingBlock: py, ...style } as CSSProperties}
      {...rest}
    >
      {children}
    </main>
  );
});
AppShellMain.displayName = 'AppShellMain';
